import { Modal } from 'antd'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAxios } from '../../core/AxiosContext'
import { useGlobalDispatch } from '../../core/GlobalContext'
import { ROUTES } from '../../core/actions'
import { DEAL } from '../../core/constants'
import { createLiquidationAction } from '../../routes/portfolio/LiquidationDuck'
import { completedContentParser } from '../../utils/portfolio/content/completedParser'
import { liquidateContentParser } from '../../utils/portfolio/content/liquidateParser'
import { ongoingContentParser } from '../../utils/portfolio/content/ongoingParser'
import { completedFooterParser } from '../../utils/portfolio/footer/completedParser'
import { liquidateFooterParser } from '../../utils/portfolio/footer/liquidateParser'
import { ongoingFooterParser } from '../../utils/portfolio/footer/ongoingParser'
import Button from '../button'
import LeaseRepaidTable from '../leaseRepaidTable'
import PdfViewer from '../pdfViewer'
import PortfolioAttribute from '../portfolioAttribute'

import { AiFillCloseCircle } from 'react-icons/ai'
import * as styles from './PortfolioCard.css'

const HERO_TYPE = 'investmentAmount'
const IRR_TYPE = 'netIRR'

const PortfolioAttributes = ({ attributes }) => {
  return (
    <div className={styles.portfolioAttributes}>
      {attributes?.map(({ type, title, val }) => (
        <PortfolioAttribute key={type} type={type} title={title} val={val} />
      ))}
    </div>
  )
}

const PortfolioFooterAttributes = ({ attributes, setModalType }) => {
  const handleURLClick = (type, title, val) => {
    switch (type) {
      case 'leaseRepaidTable':
        setModalType({ type, title, val })
        break
      default:
        // Show PDFs (Confirmation of Purchase, deal report, etc.) in an in-page popup
        setModalType({ type: 'pdf', title, val })
        break
    }
  }

  return attributes?.map(
    ({ type, title, val, primary }) =>
      val && (
        <div key={type} className={styles.footerAttributes}>
          <Button
            className={primary ? styles.footerBtnPrimary : styles.footerBtnGhost}
            type={primary ? 'primary' : 'text'}
            label={title}
            handleClick={() => handleURLClick(type, title, val)}
          />
        </div>
      )
  )
}

export const PortfolioCard = ({ portfolio, selectedTab, innerRef }) => {
  const { authAxios } = useAxios()
  const { liquidationDispatch } = useGlobalDispatch()
  const navigate = useNavigate()

  const [content, setContent] = useState([])
  const [footer, setFooter] = useState([])
  const [modalType, setModalType] = useState()

  useEffect(() => {
    switch (selectedTab) {
      // Switch case for future expansion in case of custom fields needed to be shown for each page
      case 'settled':
        setContent(completedContentParser(portfolio))
        setFooter(completedFooterParser(portfolio))
        break
      case 'liquidation_request_placed':
        setContent(liquidateContentParser(portfolio))
        setFooter(liquidateFooterParser(portfolio))
        break
      case 'liquidated':
        setContent(liquidateContentParser(portfolio))
        setFooter(liquidateFooterParser(portfolio))
        break
      default:
        setContent(ongoingContentParser(portfolio))
        setFooter(ongoingFooterParser(portfolio))
        break
    }
  }, [portfolio, selectedTab])

  const handleLiquidation = () => {
    navigate(ROUTES.LIQUIDATE)
    createLiquidationAction(portfolio?.dealCode, portfolio?.dealTransactionCode)(authAxios, liquidationDispatch)
  }

  // Presentation-only regroup of the parsed content: pull the money hero + IRR out,
  // render the remainder as attribute rows. No data-flow / field change.
  const heroAttr = content?.find(attr => attr?.type === HERO_TYPE)
  const irrAttr = content?.find(attr => attr?.type === IRR_TYPE)
  const restAttrs = content?.filter(attr => attr?.type !== HERO_TYPE && attr?.type !== IRR_TYPE)

  return (
    <div className={styles.portfolioCard} ref={innerRef}>
      {portfolio?.logo && (
        <div className={styles.watermark} aria-hidden='true'>
          <img src={portfolio?.logo} className={styles.watermarkImg} alt='' />
        </div>
      )}

      <div className={styles.cTop}>
        <img
          src={portfolio?.logo}
          className={styles.portfolioLogo}
          alt={`${portfolio?.dealTransactionCode?.slice(3, 14)}`}
        />
        {portfolio?.isAR && <span className={styles.badge}>Auto renew</span>}
      </div>

      <div className={styles.cCode}>{portfolio?.dealTransactionCode}</div>

      <div className={styles.hero}>
        {heroAttr && (
          <div className={styles.heroStat}>
            <span className={styles.heroNum}>{heroAttr?.val}</span>
            <span className={styles.heroLabel}>{heroAttr?.title}</span>
          </div>
        )}
        {irrAttr?.val != null && irrAttr?.val !== '' && (
          <>
            <div className={styles.heroSep} />
            <div className={styles.heroStat}>
              <span className={styles.irrNum}>
                {irrAttr?.val}
                <span className={styles.irrUnit}>%</span>
              </span>
              <span className={styles.heroLabel}>{irrAttr?.title}</span>
            </div>
          </>
        )}
      </div>

      <PortfolioAttributes attributes={restAttrs} />

      <div className={styles.footer}>
        <div className={styles.documents}>
          <PortfolioFooterAttributes attributes={footer} setModalType={setModalType} />
        </div>
        {/* {portfolio?.type !== DEAL.LEASE && selectedTab === 'completed' && (
          <Button label={'Liquidate Deal'} handleClick={handleLiquidation} disabled={!portfolio?.liquefiable} />
        )} */}
        {modalType?.type === 'pdf' ? (
          <PdfViewer open url={modalType.val} title={modalType.title} onClose={() => setModalType()} />
        ) : modalType ? (
          // TODO Move the modal to the root and just play with it by passing params and not have to declare it everywhere
          <Modal
            title={modalType.title}
            open={modalType}
            onCancel={() => setModalType()}
            footer={null}
            width={1000}
            closeIcon={<AiFillCloseCircle size='var(--modal-close-icon-size)' />}
            bodyStyle={{ maxHeight: '80vh', overflowY: 'scroll', padding: '1rem 1.5rem' }}>
            {modalType.type === 'leaseRepaidTable' ? (
              <LeaseRepaidTable dealTransactionCode={portfolio?.dealTransactionCode} />
            ) : null}
          </Modal>
        ) : null}
      </div>
    </div>
  )
}
